import { useState } from "react";
import { MapPin, Pencil, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { GradivostChatResponse } from "@/api/schemas";
import { buildingTypeLabel } from "@/features/feasibility/FeasibilityPanel";
import { Chip } from "@/features/feasibility/GradivostChat";
import { CoverageBadge } from "@/features/feasibility/CoverageBadge";
import { PlanUploadDialog } from "@/features/feasibility/PlanUploadDialog";
import { useGradivostChatStore } from "@/features/feasibility/useGradivostChatStore";
import { zoneSynthesis } from "@/features/feasibility/verdictSession";
import type { FeasibilityValues } from "@/features/projects/useProjectStore";

const WORK_LABELS: Record<string, string> = {
  novogradnja: "Novogradnja",
  rekonstrukcija: "Rekonstrukcija",
  dogradnja: "Dogradnja",
};

/**
 * JEDINI izvor istine za kontekst čestice — jedan red umjesto duplih chipova
 * (chat + „Parametri provjere"). Spaja identitet čestice (iz `parsed`) s
 * riješenim parametrima forme (`values`, hidratirani iz razgovora). Nosi i
 * zone-klik (promjena zone re-runa provjeru), synthesis, coverage-poruku,
 * plan-upload CTA i CoverageBadge. Klik na zonu zove ISTI `onAnswer` kao chat.
 */
export function ContextChips({
  values,
  muniName,
  onAnswer,
  onPlanActivated,
  onEditManual,
}: {
  values: FeasibilityValues;
  /** Ime općine razriješeno iz forme (kad je općina postavljena ručno, ne chatom). */
  muniName?: string | null;
  onAnswer: (resp: GradivostChatResponse, overrides?: { designation?: string }) => void;
  onPlanActivated?: (code: string, name: string) => void;
  onEditManual: () => void;
}) {
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const last = useGradivostChatStore((s) => s.lastResponse);
  const zoneMemo = useGradivostChatStore((s) => s.zoneMemo);
  const parsed = last?.parsed;

  // Zone preživljavaju iduće turnove dok je čestica ista (isti obrazac kao chat).
  const sameParcel =
    !!zoneMemo && zoneMemo.kc === (parsed?.kc ?? "") && zoneMemo.ko === (parsed?.ko ?? "");
  const zones = last?.zone?.zones?.length ? last.zone.zones : sameParcel ? zoneMemo.zones : [];
  const parcelAreaM2 = sameParcel ? zoneMemo.parcelAreaM2 : null;
  const designation = parsed?.designation ?? values.designation ?? "";
  const synthesis = zoneSynthesis(zones, designation, parcelAreaM2);
  const planMissingKo = last?.plan_missing_ko ?? null;
  const zoneMessage = last?.zone && last.zone.status !== "ok" ? last.zone.message : null;
  const offTopic = last?.off_topic === true;

  const muniCode = parsed?.municipality_code ?? values.municipality_code ?? "";
  const muniLabel = parsed?.municipality_name ?? muniName ?? parsed?.municipality_code ?? muniCode;
  const buildingType = parsed?.building_type ?? values.building_type ?? "";

  // Render samo kad postoji STVARAN kontekst (ne u čistom praznom, ne off-topic).
  if (offTopic || (!parsed && !values.municipality_code)) return null;

  return (
    <div className="mb-3" data-testid="fc-summary">
      <div className="flex flex-wrap items-center gap-1.5">
        {parsed?.kc && <Chip>čestica {parsed.kc}</Chip>}
        {parsed?.ko && <Chip>k.o. {parsed.ko}</Chip>}
        {muniCode ? (
          <Chip tone="ind">
            <MapPin className="h-3 w-3" />
            {muniLabel || muniCode}
          </Chip>
        ) : (
          <Chip tone="amber">općina nije u bazi</Chip>
        )}
        {buildingType && <Chip>{buildingTypeLabel(buildingType)}</Chip>}
        {values.work_type && <Chip>{WORK_LABELS[values.work_type] ?? values.work_type}</Chip>}
        {zones.length > 0
          ? zones.map((z, i) => (
              <Chip
                key={z.oznaka}
                tone={designation === z.oznaka ? "ind" : "stone"}
                onClick={() => last && onAnswer(last, { designation: z.oznaka })}
                title={`${z.plan_izvor ?? ""} — klik postavlja zonu`}
              >
                zona {z.oznaka} · {z.udio_pct} %
                {designation === z.oznaka && parsed?.designation_source === "auto" && i === 0
                  ? " [auto]"
                  : ""}
              </Chip>
            ))
          : designation && <Chip tone="ind">zona {designation}</Chip>}
        {values.in_city_center && <Chip>gradsko središte</Chip>}
        <CoverageBadge tier={last?.coverage_tier ?? null} />
        <button
          type="button"
          onClick={onEditManual}
          className="ml-auto inline-flex items-center gap-1 text-[0.75rem] text-stone-500 hover:text-ind-700"
        >
          <Pencil className="h-3 w-3" />
          Uredi ručno
        </button>
      </div>

      {synthesis && (
        <p className="mt-1.5 text-[0.78rem] text-stone-600" data-testid="gradivost-zone-synthesis">
          {synthesis}
        </p>
      )}

      {zoneMessage && (
        <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[0.8rem] text-amber-800">
          {zoneMessage}
        </p>
      )}

      {planMissingKo && (
        <div className="mt-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setPlanDialogOpen(true)}
            data-testid="gradivost-plan-upload-cta"
          >
            <Upload className="h-3.5 w-3.5" />
            Učitaj prostorni plan za k.o. {planMissingKo}
          </Button>
        </div>
      )}

      <PlanUploadDialog
        open={planDialogOpen}
        onOpenChange={setPlanDialogOpen}
        initialJlsName={planMissingKo ?? ""}
        onActivated={(code, name) => {
          setPlanDialogOpen(false);
          onPlanActivated?.(code, name);
        }}
      />
    </div>
  );
}
